import { useRef, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import { Button } from '@mui/material';
import { Field, FieldArray, useFormikContext } from 'formik';
import {
  GridRowModes,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridActionsCellItem,
  useGridApiRef,
} from '@mui/x-data-grid-premium';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi, InputUI, FormErrorUI, DatePickerUI } from '../Interface';
import useWindowSubtractedHeight from '../../hooks/useWindowSubtractedHeight';
import materialPlanService from '../../service/materialPlan.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';

const LineToolbar = () => {
  const { values } = useFormikContext();
  const [isExporting, setIsExporting] = useState(false);
  const fileName = values.planNo || 'anyagterv';
  const isSaved = !!values.id;

  const handleExport = async () => {
    if (!values.id || isExporting) return;

    setIsExporting(true);

    try {
      await materialPlanService.getExcelData(values.id, fileName);
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      {isSaved && (
        <Button size="small" startIcon={<DownloadIcon />} onClick={handleExport} disabled={isExporting}>
          Excel export
        </Button>
      )}
    </GridToolbarContainer>
  );
};

const lineInitialState = {
  filter: { filterModel: { items: [], logicOperator: 'and' } },
  density: 'compact',
};

const MaterialPlanLines = ({ disabled }) => {
  const apiRef = useGridApiRef();
  const focusedFieldRef = useRef(null);
  const { values, setFieldValue, errors } = useFormikContext();

  const gridHeight = useWindowSubtractedHeight({ otherContentHeight: 600, minHeight: 700 });

  // New unsaved record: qty fields + comment editable, rows deletable
  // Existing record in edit mode: only comment editable, no delete
  const canDelete = !disabled && !values.id;
  const canEditQty = !disabled && !values.id;
  const canEditComment = !disabled;
  const canEditOrderNumber = !disabled;

  const getValueIndex = (rowId) => values.materialPlanLine.findIndex((r) => (r.id ?? r.itemCode) === rowId);

  const formatQuantityListToFloat = (value) => {
    if (value === null || value === undefined || value === '') return '';

    return String(value)
      .split(',')
      .map((part) => part.trim())
      .map((part) => {
        const parsed = Number(part);
        return Number.isFinite(parsed) ? parsed.toFixed(2) : part;
      })
      .join(', ');
  };

  const columns = useMemo(
    () => (remove) =>
      dataGridPremiumColumns([
        { field: 'itemCode', type: 'string', headerName: 'Cikkszám' },
        { field: 'itemName', type: 'string', headerName: 'Cikknév' },
        { field: 'referenceList', type: 'string', headerName: 'Hivatkozás' },
        { field: 'customerOrderNumberList', type: 'string', headerName: 'Rendelésszám' },
        { field: 'customerOrderItemCodeList', type: 'string', headerName: 'Vevői rend. cikkszám' },
        { field: 'customerOrderItemNameList', type: 'string', headerName: 'Vevői rend. cikknév' },
        {
          field: 'customerOrderQuantityList',
          type: 'string',
          headerName: 'Vevői rend. mennyiség',
          renderCell: (data) => formatQuantityListToFloat(data.row.customerOrderQuantityList),
        },
        {
          field: 'ordernumber',
          type: 'string',
          headerName: 'Szállítói rendelés száma',
          editable: canEditOrderNumber,
          valueGetter: (_data, row) => {
            const idx = getValueIndex(row.id ?? row.itemCode);
            return values.materialPlanLine?.[idx]?.ordernumber ?? row.ordernumber ?? '';
          },
          renderEditCell: (data) => {
            const idx = getValueIndex(data.id);
            return (
              <Field
                name={`materialPlanLine.${idx}.ordernumber`}
                type="text"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'ordernumber'}
                helperText={<FormErrorUI message={errors?.materialPlanLine?.[idx]?.ordernumber} />}
                onChange={(e) => {
                  setFieldValue(`materialPlanLine.${idx}.ordernumber`, e.target.value);
                  data.api.setEditCellValue({ id: data.id, field: data.field, value: e.target.value });
                }}
              />
            );
          },
        },
        {
          field: 'orderDate',
          type: 'date',
          headerName: 'Rögzítés dátuma',
          editable: canEditOrderNumber,
          valueGetter: (_data, row) => {
            const idx = getValueIndex(row.id ?? row.itemCode);
            const value = values.materialPlanLine?.[idx]?.orderDate ?? row.orderDate;
            if (!value) return null;
            const parsedValue = dayjs(value);
            return parsedValue.isValid() ? parsedValue.toDate() : null;
          },
          valueFormatter: (value) => {
            return value ? dayjs(value).format('YYYY.MM.DD.') : '';
          },
          renderCell: (data) => (data.value ? dayjs(data.value).format('YYYY.MM.DD.') : ''),
          renderEditCell: (data) => {
            const idx = getValueIndex(data.id);
            const value = values.materialPlanLine?.[idx]?.orderDate ?? data.row.orderDate;

            return (
              <DatePickerUI
                value={value ? dayjs(value) : null}
                helperText={<FormErrorUI message={errors?.materialPlanLine?.[idx]?.orderDate} />}
                onChange={(newValue) => {
                  const nextValue = newValue ? dayjs(newValue).format('YYYY-MM-DD') : null;
                  setFieldValue(`materialPlanLine.${idx}.orderDate`, nextValue);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: newValue ? dayjs(newValue).toDate() : null,
                  });
                }}
              />
            );
          },
        },
        { field: 'requiredQty', type: 'number', headerName: 'Szükséges menny.' },
        { field: 'onStock', type: 'number', headerName: 'Készleten' },
        { field: 'reserved', type: 'number', headerName: 'Foglalt' },
        { field: 'suggestedQty', type: 'number', headerName: 'Javasolt menny.' },

        {
          field: 'fromStockQty',
          type: 'number',
          headerName: 'Készletből',
          editable: canEditQty,
          valueGetter: (_data, row) => {
            const idx = getValueIndex(row.id ?? row.itemCode);
            return values.materialPlanLine?.[idx]?.fromStockQty ?? row.fromStockQty;
          },
          renderEditCell: (data) => {
            const idx = getValueIndex(data.id);
            return (
              <Field
                name={`materialPlanLine.${idx}.fromStockQty`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'fromStockQty'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.materialPlanLine?.[idx]?.fromStockQty} />}
                onChange={(e) => {
                  setFieldValue(`materialPlanLine.${idx}.fromStockQty`, e.target.value);
                  data.api.setEditCellValue({ id: data.id, field: data.field, value: e.target.value });
                }}
              />
            );
          },
        },

        {
          field: 'toBeOrderedQty',
          type: 'number',
          headerName: 'Rendelendő',
          editable: canEditQty,
          valueGetter: (_data, row) => {
            const idx = getValueIndex(row.id ?? row.itemCode);
            return values.materialPlanLine?.[idx]?.toBeOrderedQty ?? row.toBeOrderedQty;
          },
          renderEditCell: (data) => {
            const idx = getValueIndex(data.id);
            return (
              <Field
                name={`materialPlanLine.${idx}.toBeOrderedQty`}
                type="number"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'toBeOrderedQty'}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                helperText={<FormErrorUI message={errors?.materialPlanLine?.[idx]?.toBeOrderedQty} />}
                onChange={(e) => {
                  setFieldValue(`materialPlanLine.${idx}.toBeOrderedQty`, e.target.value);
                  data.api.setEditCellValue({ id: data.id, field: data.field, value: e.target.value });
                }}
              />
            );
          },
        },

        {
          field: 'comment',
          type: 'string',
          headerName: 'Megjegyzés',
          editable: canEditComment,
          valueGetter: (_data, row) => {
            const idx = getValueIndex(row.id ?? row.itemCode);
            return values.materialPlanLine?.[idx]?.comment ?? row.comment;
          },
          renderEditCell: (data) => {
            const idx = getValueIndex(data.id);
            return (
              <Field
                name={`materialPlanLine.${idx}.comment`}
                type="text"
                as={InputUI}
                variant="standard"
                fullWidth
                autoFocus={focusedFieldRef.current === 'comment'}
                helperText={<FormErrorUI message={errors?.materialPlanLine?.[idx]?.comment} />}
                onChange={(e) => {
                  setFieldValue(`materialPlanLine.${idx}.comment`, e.target.value);
                  data.api.setEditCellValue({ id: data.id, field: data.field, value: e.target.value });
                }}
              />
            );
          },
        },

        ...(canDelete
          ? [
              {
                field: 'actions',
                type: 'actions',
                headerName: '',
                cellClassName: 'actions',
                sortable: false,
                disableExport: true,
                getActions: ({ id }) => [
                  <GridActionsCellItem
                    icon={<DeleteIcon />}
                    label="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      const idx = getValueIndex(id);
                      remove(idx);
                    }}
                    color="inherit"
                  />,
                ],
              },
            ]
          : []),
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values.materialPlanLine, errors, setFieldValue, canEditQty, canEditComment, canEditOrderNumber, canDelete]
  );

  return (
    <FieldArray name="materialPlanLine">
      {({ remove }) => (
        <DatagridPremiumUi
          apiRef={apiRef}
          datagridIdentifier={DataGridIdentifiers.MaterialPlanLineView}
          datagridInitialState={lineInitialState}
          height={gridHeight}
          rows={values.materialPlanLine}
          columns={columns(remove)}
          editMode="row"
          paginationAndfilterMode="client"
          disableSave
          getRowId={(row) => row.id ?? row.itemCode}
          slots={{ toolbar: LineToolbar }}
          onCellDoubleClick={(_params, event) => {
            event.defaultMuiPrevented = true;
          }}
          onCellClick={(params) => {
            if (!canEditQty && !canEditComment && !canEditOrderNumber) return;
            focusedFieldRef.current = params.field;

            const rowsInEditMode = Object.keys(apiRef.current.state.editRows);
            for (const rowId of rowsInEditMode) {
              if (params.row.id?.toString() !== rowId && params.row.itemCode?.toString() !== rowId) {
                apiRef.current.stopRowEditMode({ id: rowId });
              }
            }

            const rowMode = apiRef.current.getRowMode(params.id);
            if (rowMode === GridRowModes.View) {
              const field = params.field;
              const isEditable =
                (field === 'ordernumber' && canEditOrderNumber) ||
                (field === 'orderDate' && canEditOrderNumber) ||
                (field === 'comment' && canEditComment) ||
                (['fromStockQty', 'toBeOrderedQty'].includes(field) && canEditQty);
              if (isEditable) {
                apiRef.current.startRowEditMode({ id: params.id });
              }
            }
          }}
          onRowEditStop={() => {
            focusedFieldRef.current = null;
          }}
          headerFilters
          getRowHeight={() => 'auto'}
        />
      )}
    </FieldArray>
  );
};

export default MaterialPlanLines;
